import { createContext, useState, useCallback, useMemo, type FC, type PropsWithChildren } from "react";
import { ShareResourceModal } from "@/components/share/ShareResourceModal";
import type { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";

// Types
export type ShareableResourceType = ResourceType.PROJECT | ResourceType.RUNNER | ResourceType.WORKFLOW | ResourceType.GROUP;

export type ShareableResource = {
    resourceType: ShareableResourceType;
    resourceId: string;
    resourceName: string;
};

type ShareResourceContextType = {
    /** Currently shared resource (null if modal closed) */
    resource: ShareableResource | null;
    /** Is the share modal currently open? */
    isOpen: boolean;
    /** Open share dialog for a resource */
    openShareDialog: (resource: ShareableResource) => void;
    /** Close share dialog */
    closeShareDialog: () => void;
};

// Context
export const ShareResourceContext = createContext<ShareResourceContextType>({
    resource: null,
    isOpen: false,
    openShareDialog: () => undefined,
    closeShareDialog: () => undefined,
});

// Provider
export const ShareResourceModalProvider: FC<PropsWithChildren> = ({ children }) => {
    const [resource, setResource] = useState<ShareableResource | null>(null);

    const openShareDialog = useCallback((resource: ShareableResource) => {
        setResource(resource);
    }, []);

    const closeShareDialog = useCallback(() => {
        setResource(null);
    }, []);

    const value = useMemo(
        () => ({
            resource,
            isOpen: resource !== null,
            openShareDialog,
            closeShareDialog,
        }),
        [resource, openShareDialog, closeShareDialog],
    );

    return (
        <ShareResourceContext.Provider value={value}>
            {children}
            <ShareResourceModal />
        </ShareResourceContext.Provider>
    );
};
